import * as signalR from '@microsoft/signalr';
import {
  getSignalRErrorMessage,
  logSignalR,
  logSignalRError,
  logSignalRWarn,
} from '../utils/signalRDiagnostics';

export interface WorkflowExecutionEvents {
  onWorkflowNodeStarted?: (data: { executionId: string; nodeId: string; nodeType: string; label?: string; timestamp?: string }) => void;
  onWorkflowNodeCompleted?: (data: { executionId: string; nodeId: string; outputJson: string; stateJson: string; timestamp?: string }) => void;
  onWorkflowNodeFailed?: (data: { executionId: string; nodeId: string; errorMessage: string; timestamp?: string }) => void;
  onWorkflowExecutionStatusChanged?: (data: { executionId: string; status: string; currentNodeId?: string; errorMessage?: string; timestamp?: string }) => void;
}

export interface SignalREvents {
  onMessageReceived?: (data: { sessionId: string; message: string }) => void;
  onAgentThinking?: (data: { sessionId: string }) => void;
  onTokenReceived?: (data: { sessionId: string; token: string }) => void;
  onToolCall?: (data: { sessionId: string; toolName: string; arguments: string; toolCallId?: string | null }) => void;
  onToolResult?: (data: { sessionId: string; toolCallId: string; toolName: string; success: boolean }) => void;
  onResponseComplete?: (data: { sessionId: string; response: string }) => void;
  onExecutionError?: (data: { sessionId: string; errorMessage: string; source?: string; timestamp?: string }) => void;
  onSubAgentStarted?: (data: { sessionId: string; callId: string; alias: string; depth: number; task: string }) => void;
  onSubAgentTokenReceived?: (data: { sessionId: string; callId: string; token: string }) => void;
  onSubAgentToolCall?: (data: { sessionId: string; callId: string; toolName: string; arguments: string }) => void;
  onSubAgentCompleted?: (data: { sessionId: string; callId: string; result: string; success: boolean; errorMessage?: string }) => void;
  onHarnessStateUpdated?: (data: { sessionId: string; mode: string; todosJson?: string | null }) => void;
}

/**
 * Service for SignalR connection management.
 */
class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private events: SignalREvents = {};
  private workflowEvents: WorkflowExecutionEvents = {};
  private _onReconnecting: (() => void) | null = null;
  private _onReconnected: (() => void) | null = null;
  private _onClosed: ((error?: Error) => void) | null = null;
  private lastErrorMessage: string | null = null;

  /**
   * Connects to the SignalR hub (WebSockets only — no Long Polling).
   */
  async connect(baseUrl: string, token: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    const hubUrl = `${baseUrl}/hubs/agentExecution`;

    try {
      if (this.connection) {
        try {
          await this.connection.stop();
        } catch {
          // Ignore stop failures during rebuild.
        }
        this.connection = null;
      }

      logSignalR('agentExecution', 'Connecting', {
        hubUrl,
        transport: 'WebSockets',
      });

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
              const delay = this.reconnectDelay * Math.pow(2, retryContext.previousRetryCount);
              logSignalRWarn('agentExecution', 'Scheduling reconnect', retryContext.retryReason, {
                previousRetryCount: retryContext.previousRetryCount,
                elapsedMs: retryContext.elapsedMilliseconds,
                nextDelayMs: delay,
              });
              return delay;
            }
            logSignalRError(
              'agentExecution',
              'Max automatic reconnect attempts reached',
              retryContext.retryReason,
              {
                previousRetryCount: retryContext.previousRetryCount,
                elapsedMs: retryContext.elapsedMilliseconds,
              },
            );
            return null; // Stop reconnecting
          },
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // The server pings every 2.5 min (KeepAliveInterval in Program.cs); the SignalR default of
      // 30s would declare the connection dead between pings.
      this.connection.serverTimeoutInMilliseconds = 6 * 60 * 1000;

      // Set up event handlers
      this.setupEventHandlers();

      // Handle connection state changes
      this.connection.onclose((error) => {
        this.isConnecting = false;
        if (error) {
          this.lastErrorMessage = getSignalRErrorMessage(error, 'Agent execution hub closed');
          logSignalRError('agentExecution', 'Connection closed with error', error, {
            connectionId: this.connection?.connectionId,
          });
          this.attemptReconnect(baseUrl, token);
        } else {
          logSignalR('agentExecution', 'Connection closed', {
            connectionId: this.connection?.connectionId,
          });
        }
        this._onClosed?.(error);
      });

      this.connection.onreconnecting((error) => {
        this.lastErrorMessage = getSignalRErrorMessage(error, 'Reconnecting to agent execution hub…');
        logSignalRWarn('agentExecution', 'Reconnecting', error, {
          connectionId: this.connection?.connectionId,
          state: this.connection?.state,
        });
        this._onReconnecting?.();
      });

      this.connection.onreconnected((connectionId) => {
        this.reconnectAttempts = 0;
        this.lastErrorMessage = null;
        logSignalR('agentExecution', 'Reconnected', {
          connectionId,
          transport: 'WebSockets',
          state: this.connection?.state,
        });
        this._onReconnected?.();
      });

      await this.connection.start();
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.lastErrorMessage = null;
      logSignalR('agentExecution', 'Connected', {
        hubUrl,
        transport: 'WebSockets',
        connectionId: this.connection.connectionId,
        state: this.connection.state,
      });
    } catch (error) {
      this.isConnecting = false;
      this.lastErrorMessage = getSignalRErrorMessage(error, 'Failed to connect to agent execution hub');
      logSignalRError('agentExecution', 'Connect failed', error, {
        hubUrl,
        transport: 'WebSockets',
        hint: 'WebSockets-only. With Azure SignalR, negotiate stays on the API and WS should go to *.service.signalr.net. Check Azure:SignalR:ConnectionString, SignalR App Server RBAC on the API MI, CORS, and auth token.',
      });
      throw error;
    }
  }

  /**
   * Disconnects from the SignalR hub.
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      logSignalR('agentExecution', 'Disconnecting', { connectionId: this.connection.connectionId });
      await this.connection.stop();
      this.connection = null;
      this.events = {};
      this.lastErrorMessage = null;
    }
  }

  getLastErrorMessage(): string | null {
    return this.lastErrorMessage;
  }

  /**
   * Joins a session group.
   */
  async joinSession(sessionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('JoinSession', sessionId);
    }
  }

  /**
   * Leaves a session group.
   */
  async leaveSession(sessionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('LeaveSession', sessionId);
    }
  }

  /**
   * Joins a workflow execution group (composer live canvas).
   */
  async joinWorkflowExecution(executionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('JoinWorkflowExecution', executionId);
    }
  }

  /**
   * Leaves a workflow execution group.
   */
  async leaveWorkflowExecution(executionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('LeaveWorkflowExecution', executionId);
    }
  }

  /**
   * Registers event handlers.
   */
  on(events: SignalREvents): void {
    this.events = { ...this.events, ...events };
  }

  /**
   * Unregisters all event handlers.
   */
  off(): void {
    this.events = {};
  }

  /**
   * Registers workflow execution event handlers. Kept separate from the
   * session events so the composer does not clobber chat streaming handlers.
   */
  onWorkflowEvents(events: WorkflowExecutionEvents): void {
    this.workflowEvents = { ...this.workflowEvents, ...events };
  }

  /**
   * Unregisters all workflow execution event handlers.
   */
  offWorkflowEvents(): void {
    this.workflowEvents = {};
  }

  /**
   * Gets the connection state.
   */
  getState(): signalR.HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  /**
   * Sets callbacks for connection state transitions triggered by
   * the built-in automatic reconnect mechanism.
   */
  setReconnectionCallbacks(
    onReconnecting: (() => void) | null,
    onReconnected: (() => void) | null,
    onClosed?: ((error?: Error) => void) | null,
  ): void {
    this._onReconnecting = onReconnecting;
    this._onReconnected = onReconnected;
    this._onClosed = onClosed ?? null;
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    this.connection.on('MessageReceived', (data: { sessionId: string; message: string }) => {
      this.events.onMessageReceived?.(data);
    });

    this.connection.on('AgentThinking', (data: { sessionId: string }) => {
      this.events.onAgentThinking?.(data);
    });

    this.connection.on('TokenReceived', (data: { sessionId: string; token: string }) => {
      this.events.onTokenReceived?.(data);
    });

    this.connection.on('ToolCall', (data: { sessionId: string; toolName: string; arguments: string; toolCallId?: string | null }) => {
      this.events.onToolCall?.(data);
    });

    this.connection.on('ToolResult', (data: { sessionId: string; toolCallId: string; toolName: string; success: boolean }) => {
      this.events.onToolResult?.(data);
    });

    this.connection.on('ResponseComplete', (data: { sessionId: string; response: string }) => {
      this.events.onResponseComplete?.(data);
    });

    this.connection.on('ExecutionError', (data: { sessionId: string; errorMessage: string; source?: string; timestamp?: string }) => {
      this.events.onExecutionError?.(data);
    });

    this.connection.on('SubAgentStarted', (data: { sessionId: string; callId: string; alias: string; depth: number; task: string }) => {
      this.events.onSubAgentStarted?.(data);
    });

    this.connection.on('SubAgentTokenReceived', (data: { sessionId: string; callId: string; token: string }) => {
      this.events.onSubAgentTokenReceived?.(data);
    });

    this.connection.on('SubAgentToolCall', (data: { sessionId: string; callId: string; toolName: string; arguments: string }) => {
      this.events.onSubAgentToolCall?.(data);
    });

    this.connection.on('SubAgentCompleted', (data: { sessionId: string; callId: string; result: string; success: boolean; errorMessage?: string }) => {
      this.events.onSubAgentCompleted?.(data);
    });

    this.connection.on('HarnessStateUpdated', (data: { sessionId: string; mode: string; todosJson?: string | null }) => {
      this.events.onHarnessStateUpdated?.(data);
    });

    this.connection.on('WorkflowNodeStarted', (data: { executionId: string; nodeId: string; nodeType: string; label?: string; timestamp?: string }) => {
      this.workflowEvents.onWorkflowNodeStarted?.(data);
    });

    this.connection.on('WorkflowNodeCompleted', (data: { executionId: string; nodeId: string; outputJson: string; stateJson: string; timestamp?: string }) => {
      this.workflowEvents.onWorkflowNodeCompleted?.(data);
    });

    this.connection.on('WorkflowNodeFailed', (data: { executionId: string; nodeId: string; errorMessage: string; timestamp?: string }) => {
      this.workflowEvents.onWorkflowNodeFailed?.(data);
    });

    this.connection.on('WorkflowExecutionStatusChanged', (data: { executionId: string; status: string; currentNodeId?: string; errorMessage?: string; timestamp?: string }) => {
      this.workflowEvents.onWorkflowExecutionStatusChanged?.(data);
    });
  }

  private async attemptReconnect(baseUrl: string, token: string): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.lastErrorMessage = 'Max reconnect attempts reached for agent execution hub';
      logSignalRError('agentExecution', 'Max reconnect attempts reached', this.lastErrorMessage, {
        maxReconnectAttempts: this.maxReconnectAttempts,
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    logSignalRWarn('agentExecution', 'Manual reconnect attempt', undefined, {
      attempt: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      delayMs: delay,
    });
    await new Promise((resolve) => setTimeout(resolve, delay));

    try {
      await this.connect(baseUrl, token);
    } catch (error) {
      this.lastErrorMessage = getSignalRErrorMessage(error, 'Reconnection failed');
      logSignalRError('agentExecution', 'Manual reconnection failed', error, {
        attempt: this.reconnectAttempts,
      });
    }
  }
}

export const signalRService = new SignalRService();
